import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Calendar, Clock, Award, CheckCircle2, 
  Play, Pause, Download, FileText, Upload, Video, ChevronRight, 
  Star, User, Sparkles, AlertCircle, X, Check, Laptop, ShieldCheck,
  Lock, ArrowRight, CornerRightDown, ExternalLink, HelpCircle, FileCheck,
  Layers, Bookmark, MessageSquare, RefreshCw, Search, ChevronDown,
  Volume2, VolumeX, Maximize2, RotateCcw, Share2, Printer, ArrowLeft,
  Sliders, Compass, Eye, Edit3, CheckSquare, BrainCircuit, Bell
} from 'lucide-react';
import { DEMO_STUDENTS, getModuleFacilitator } from '../../data/portalData';
import { SiliconAssistant } from './SiliconAssistant';
import { CandidateAccountDrawer } from './CandidateAccountDrawer';
import { FacultyMessagingDesk } from './FacultyMessagingDesk';

export const StudentDashboard = ({ selectedStudentId = 'student-dba', onSelectStudent }) => {
  const [activeStudentId, setActiveStudentId] = useState(selectedStudentId);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState('overview'); // overview, video, visual, practice, quiz, flashcards, silicon, resources, certificate, faculty-desk
  const [mediaMode, setMediaMode] = useState('video'); // 'video' | 'visual'
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [videoTimestamp, setVideoTimestamp] = useState(15); // minutes
  const [videoSpeed, setVideoSpeed] = useState('1.0x');
  const [isSiliconOpen, setIsSiliconOpen] = useState(false);
  const [selectedSiliconPrompt, setSelectedSiliconPrompt] = useState(null);
  const [candidateDrawerOpen, setCandidateDrawerOpen] = useState(false);
  const [updatesModalOpen, setUpdatesModalOpen] = useState(false);
  const [aboutCourseModalOpen, setAboutCourseModalOpen] = useState(false);
  const [facultyModalOpen, setFacultyModalOpen] = useState(false);

  // Practice & Notes State
  const [scholarNotes, setScholarNotes] = useState(
    "Key takeaway from Prof. Johnson: In executive business inquiry, ontology (what exists) dictates epistemology (what can be known). As corporate executives, Saunders' Research Onion prevents methodological mismatch between theoretical rigour and boardroom relevance."
  );
  const [notesSaved, setNotesSaved] = useState(false);

  // Knowledge Check Quiz State
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  // Flashcards State
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isCardFlipped, setIsCardFlipped] = useState(false);

  // Sync if prop changes
  useEffect(() => {
    if (selectedStudentId) {
      setActiveStudentId(selectedStudentId);
    }
  }, [selectedStudentId]);

  const student = DEMO_STUDENTS.find(s => s.id === activeStudentId) || DEMO_STUDENTS[0];
  const activeCourse = student.activeModule || student.modules[0];
  const modules = activeCourse.lessons || [];
  const currentModule = modules[activeModuleIndex] || modules[0];
  const currentFacilitator = currentModule?.facilitator || getModuleFacilitator(currentModule, activeModuleIndex);

  const handleStudentSwitch = (id) => {
    setActiveStudentId(id);
    if (onSelectStudent) {
      onSelectStudent(id);
    }
    setActiveModuleIndex(0);
    setIsPlayingVideo(false);
    setQuizSubmitted(false);
    setQuizAnswers({});
  };

  const handleSaveNotes = () => {
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2500);
  };

  // Filter modules based on search input
  const filteredModules = modules.filter(m => 
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.keyTakeaway?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Flashcard items for the active module
  const flashcards = [
    {
      term: "Ontology",
      question: "What is Ontology in Executive Doctoral Research?",
      definition: "The branch of philosophy concerning the nature of social reality. It asks: Does reality exist independently of human consciousness (Realism), or is it constructed through human perception and discourse (Nominalism / Social Constructionism)?",
      context: "Essential for deciding whether market disruptions can be measured objectively or understood through managerial perspectives."
    },
    {
      term: "Epistemology",
      question: "What constitutes valid Epistemological grounding?",
      definition: "The study of knowledge and justified belief. It asks: What constitutes legitimate, valid, and authoritative knowledge in corporate management, and how can an executive researcher communicate that knowledge?",
      context: "Distinguishes between Positivist hard data metrics and Interpretivist qualitative managerial meanings."
    },
    {
      term: "Saunders' Research Onion",
      question: "What are the 6 concentric layers of Saunders' Onion?",
      definition: "1. Research Philosophy, 2. Research Approach, 3. Methodological Choice, 4. Research Strategy, 5. Time Horizon, 6. Techniques & Procedures.",
      context: "Mandatory framework for AKSU DBA dissertation proposals to prevent methodological fragmentation."
    },
    {
      term: "Methodological Triangulation",
      question: "Why is Triangulation favored in Executive Inquiries?",
      definition: "Using multiple data sources, researchers, or methods (e.g. combining financial econometrics with in-depth CEO interviews) to corroborate findings and eliminate single-source bias.",
      context: "Elevates practitioner credibility in board presentations and academic Viva Voce examinations."
    }
  ];

  // Knowledge Check Questions
  const quizQuestions = [
    {
      id: "q1",
      question: "An executive researcher seeking to measure the exact quantitative impact of board gender diversity on quarterly ROI across 100 quoted firms is operating primarily under which philosophical stance?",
      options: [
        { key: "A", text: "Social Constructionism" },
        { key: "B", text: "Positivism" },
        { key: "C", text: "Postmodern Interpretivism" },
        { key: "D", text: "Phenomenology" }
      ],
      correctKey: "B",
      explanation: "Positivism focuses on objective, measurable reality and law-like generalisations through quantitative hypothesis testing."
    },
    {
      id: "q2",
      question: "Under Saunders' Research Onion, if your investigation progresses from observed corporate data to pattern recognition and subsequent theory building, your research approach is:",
      options: [
        { key: "A", text: "Purely Deductive" },
        { key: "B", text: "Inductive" },
        { key: "C", text: "Experimental" },
        { key: "D", text: "Axiological" }
      ],
      correctKey: "B",
      explanation: "The inductive approach moves from specific operational observations to broader generalisations and conceptual theory formulation."
    },
    {
      id: "q3",
      question: "Why is Pragmatism considered particularly advantageous for doctoral executive candidates (DBA) at AKSU Business School?",
      options: [
        { key: "A", text: "It strictly forbids the collection of statistical data" },
        { key: "B", text: "It bridges theoretical rigour with actionable practitioner solutions using mixed methods" },
        { key: "C", text: "It removes the requirement for a literature review" },
        { key: "D", text: "It eliminates the need for doctoral supervisory approval" }
      ],
      correctKey: "B",
      explanation: "Pragmatism focuses on 'what works' to address complex organisational problems, enabling executives to blend qualitative insights with quantitative rigor."
    }
  ];

  const handleQuizSelect = (qId, optionKey) => {
    if (quizSubmitted) return;
    setQuizAnswers(prev => ({ ...prev, [qId]: optionKey }));
  };

  const handleQuizSubmit = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correctKey) {
        score += 1;
      }
    });
    setQuizScore(score);
    setQuizSubmitted(true);
  };

  const handleResetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
  };

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900 font-sans pb-16">
      
      {/* 4 Demo Student Programme Switcher (Top Ribbon) */}
      <div className="bg-white border-b border-slate-200 px-4 py-2.5">
        <div className="w-full px-2 sm:px-4 lg:px-6 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center space-x-2">
            <span className="w-2 h-2 rounded-full bg-[#7A470E] animate-pulse" />
            <span className="font-sc font-bold text-slate-700 uppercase tracking-wider text-[11px]">
              Enrolled Candidate Track:
            </span>
            <button
              onClick={() => setCandidateDrawerOpen(true)}
              className="flex items-center space-x-2 px-2.5 py-1 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-slate-900 transition-all cursor-pointer shadow-2xs group"
              title="Click to open Candidate Account Menu (Profile, Bursary, Grades, ID Pass)"
            >
              <img 
                src={student.avatar} 
                alt={student.name} 
                className="w-5 h-5 rounded-full object-cover border border-[#7A470E]" 
              />
              <span className="font-bold text-[#0B2644] font-sc">
                {student.degreeLevel} · {student.fullName}
              </span>
              <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 px-1.5 py-0.2 rounded font-bold">
                ● Cleared
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#7A470E] transition-transform" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 font-sc">
            <span className="text-[11px] text-slate-500 mr-1 hidden sm:inline">Switch Track:</span>
            {DEMO_STUDENTS.map((s) => (
              <button
                key={s.id}
                onClick={() => handleStudentSwitch(s.id)}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  activeStudentId === s.id
                    ? 'bg-[#0B2644] text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-amber-50 hover:text-[#7A470E]'
                }`}
              >
                <span>{s.degreeLevel}: {s.fullName.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* TOP APPLICATION BAR (Exact Adaptation of TrustStack LMS) */}
      {/* ======================================================== */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-14 z-20 shadow-xs">
        <div className="w-full px-4 sm:px-6 lg:px-6 py-3 flex items-center justify-between gap-4">
          
          {/* Left Brand Identity & Course Indicator */}
          <div className="flex items-center space-x-4 shrink-0">
            <div className="flex items-center space-x-2.5">
              <img 
                src="/Logo.png" 
                alt="AKSU Crest" 
                className="w-9 h-9 object-contain logo-glow" 
              />
              <div className="hidden sm:block">
                <div className="text-sm font-black font-sc text-[#0B2644] leading-none">
                  AKSU Business School
                </div>
                <div className="text-[10px] font-bold font-sc uppercase tracking-widest text-[#7A470E] mt-0.5">
                  Executive LMS
                </div>
              </div>
            </div>

            <div className="h-6 w-px bg-slate-200 hidden md:block" />

            {/* Course Tag & Title */}
            <div className="hidden md:flex items-center space-x-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-slate-100 text-[#0B2644] border border-slate-300 font-sc">
                Course
              </span>
              <span className="text-xs sm:text-sm font-bold text-slate-800 font-sc truncate max-w-xs xl:max-w-md">
                {activeCourse.code}: {activeCourse.title}
              </span>
            </div>
          </div>

          {/* Center Progress & Action Tools */}
          <div className="flex items-center space-x-3 lg:space-x-4">
            
            {/* Progress Meter */}
            <div className="hidden lg:flex items-center space-x-2.5 text-xs font-sc font-bold">
              <div className="text-right">
                <span className="text-[#7A470E]">16% Complete</span>
                <span className="text-[10px] text-slate-400 block font-normal font-sans">(1/6 modules)</span>
              </div>
              <div className="w-20 bg-slate-200 h-2 rounded-full overflow-hidden">
                <div className="bg-[#7A470E] h-2 rounded-full" style={{ width: '16%' }} />
              </div>
            </div>

            {/* Ask Silicon AI Assistant Button */}
            <button
              onClick={() => {
                setSelectedSiliconPrompt(null);
                setIsSiliconOpen(true);
              }}
              className="px-3.5 py-1.5 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-900 border border-purple-200 font-bold text-xs flex items-center space-x-1.5 transition-all shadow-xs cursor-pointer font-sc hover:scale-[1.02]"
              title="Open Silicon AI Learning Assistant"
            >
              <BrainCircuit className="w-4 h-4 text-purple-700 animate-pulse" />
              <span>Ask Silicon AI</span>
            </button>

            {/* Close / Open Sidebar Contents Toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="px-3 py-1.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold font-sc flex items-center space-x-1.5 transition-colors cursor-pointer"
            >
              {sidebarOpen ? (
                <>
                  <X className="w-3.5 h-3.5 text-slate-500" />
                  <span className="hidden sm:inline">Close contents</span>
                </>
              ) : (
                <>
                  <Layers className="w-3.5 h-3.5 text-[#7A470E]" />
                  <span>Course contents</span>
                </>
              )}
            </button>
          </div>

          {/* Right Assessment Signal & Actions */}
          <div className="flex items-center space-x-2.5 shrink-0">
            
            {/* Certificate Requirement Box (TrustStack Callout) */}
            <div className="hidden xl:flex items-center space-x-2 px-3 py-1 rounded-xl bg-amber-50/70 border border-[#7A470E]/30 text-[11px]">
              <div className="text-left leading-tight">
                <span className="font-bold text-[#7A470E] block font-sc">Doctoral Credit Gate</span>
                <span className="text-[10px] text-slate-500">Complete all 6 module requirements</span>
              </div>
              <button 
                onClick={() => setActiveSubTab('certificate')}
                className="px-2 py-1 rounded bg-[#0B2644] text-white font-bold text-[10px] font-sc hover:bg-[#06182B] cursor-pointer"
              >
                Track Status
              </button>
            </div>

            {/* Updates Button */}
            <button
              onClick={() => setUpdatesModalOpen(true)}
              className="px-3 py-1.5 rounded-xl bg-amber-100 hover:bg-amber-200 text-[#5C3306] font-bold text-xs flex items-center space-x-1 transition-colors cursor-pointer font-sc shadow-2xs"
            >
              <Bell className="w-3.5 h-3.5 text-[#7A470E]" />
              <span className="hidden sm:inline">Updates</span>
            </button>

            {/* About Course Link */}
            <button
              onClick={() => setAboutCourseModalOpen(true)}
              className="hidden md:inline-flex items-center text-xs font-bold text-slate-600 hover:text-[#0B2644] font-sc space-x-1 cursor-pointer"
            >
              <span>About course</span>
              <ExternalLink className="w-3 h-3" />
            </button>

          </div>

        </div>
      </header>

      {/* ======================================================== */}
      {/* MAIN LEARNING STAGE LAYOUT (Sidebar + Player Canvas + Silicon) */}
      {/* ======================================================== */}
      <div className="w-full px-3 sm:px-5 lg:px-6 mt-6">
        <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 items-start">
          
          {/* ===================================================== */}
          {/* LEFT SIDEBAR: COURSE CONTENTS                         */}
          {/* ===================================================== */}
          {sidebarOpen && (
            <aside className="w-full lg:w-76 xl:w-80 shrink-0 bg-white rounded-3xl border-2 border-slate-200 p-5 shadow-sm space-y-4 animate-in fade-in duration-200">
              
              {/* Sidebar Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#7A470E] font-sc block">
                    Course Contents
                  </span>
                  <h2 className="text-lg font-black font-sc text-[#0B2644] leading-snug">
                    {activeCourse.title}
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {modules.length} modules • ~60 hours • Executive Weekend
                  </p>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer lg:hidden"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Search Topics or Concepts */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search topics or concepts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs rounded-xl border border-slate-200 focus:outline-none focus:border-[#7A470E] focus:ring-1 focus:ring-[#7A470E] bg-slate-50 placeholder-slate-400"
                />
              </div>

              {/* Progress & Certificate Banner */}
              <button
                onClick={() => setActiveSubTab('certificate')}
                className="w-full text-left p-3 rounded-2xl bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200/80 hover:border-purple-300 transition-all flex items-center justify-between group cursor-pointer"
              >
                <div>
                  <span className="text-xs font-bold text-purple-900 block font-sc group-hover:text-purple-950">
                    My Progress & Transcript (1/6) →
                  </span>
                  <span className="text-[10px] text-purple-700">
                    Continuous Assessment Active
                  </span>
                </div>
                <Award className="w-5 h-5 text-purple-600 group-hover:scale-110 transition-transform" />
              </button>

              {/* Module Items List */}
              <div className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
                {filteredModules.map((mod, idx) => {
                  const isActive = activeModuleIndex === idx;
                  const isCompleted = mod.completed;
                  const isInProgress = mod.current;

                  return (
                    <div 
                      key={mod.id || idx}
                      className={`rounded-2xl border transition-all overflow-hidden ${
                        isActive
                          ? 'border-[#7A470E] bg-amber-50/40 shadow-xs'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      {/* Module Header Button */}
                      <button
                        onClick={() => {
                          setActiveModuleIndex(idx);
                          setIsPlayingVideo(false);
                        }}
                        className="w-full text-left p-3.5 flex items-start space-x-3 cursor-pointer select-none"
                      >
                        {/* Number / Status Icon */}
                        <div className={`w-7 h-7 rounded-xl flex items-center justify-center font-mono font-bold text-xs shrink-0 mt-0.5 ${
                          isCompleted
                            ? 'bg-emerald-600 text-white'
                            : isActive
                            ? 'bg-[#7A470E] text-white shadow-xs'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {isCompleted ? <Check className="w-4 h-4 stroke-[3]" /> : `0${idx + 1}`}
                        </div>

                        {/* Title & Metadata */}
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-xs font-bold leading-snug font-sc ${
                            isActive ? 'text-[#0B2644] font-black' : 'text-slate-800'
                          }`}>
                            {mod.title}
                          </h3>
                          <div className="text-[10px] text-slate-500 mt-1 flex items-center space-x-2">
                            <span>{mod.duration}</span>
                            <span>•</span>
                            <span className={`font-semibold ${
                              isCompleted 
                                ? 'text-emerald-700' 
                                : isInProgress 
                                ? 'text-[#7A470E]' 
                                : 'text-slate-400'
                            }`}>
                              {mod.status}
                            </span>
                          </div>

                          {/* Module Course Facilitator Chip */}
                          {(() => {
                            const modFac = mod.facilitator || getModuleFacilitator(mod, idx);
                            return (
                              <div className="flex items-center space-x-1.5 mt-2 pt-1.5 border-t border-slate-100 text-[10px]">
                                <img 
                                  src={modFac?.avatar} 
                                  alt={modFac?.name} 
                                  className="w-4 h-4 rounded-full object-cover border border-[#7A470E] shrink-0" 
                                />
                                <span className="text-slate-600 font-sc truncate">
                                  Facilitator: <strong className="text-[#0B2644] font-bold">{modFac?.name?.split(',')[0]}</strong>
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </button>

                      {/* Expanded Sub-items (When Active - TrustStack Style) */}
                      {isActive && (
                        <div className="px-4 pb-3 pt-1 border-t border-[#7A470E]/20 space-y-1 bg-white/70 text-xs">
                          
                          <button
                            onClick={() => { setMediaMode('video'); setActiveSubTab('video'); }}
                            className={`w-full text-left py-1.5 px-2 rounded-lg flex items-center justify-between transition-colors cursor-pointer ${
                              mediaMode === 'video' && activeSubTab === 'video' 
                                ? 'bg-amber-100/70 text-[#7A470E] font-bold' 
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <span className="flex items-center space-x-2">
                              <Play className="w-3 h-3 text-[#7A470E]" />
                              <span>Video lecture</span>
                            </span>
                            <span className="text-[10px] font-mono text-slate-400">{mod.duration}</span>
                          </button>

                          <button
                            onClick={() => { setMediaMode('visual'); setActiveSubTab('visual'); }}
                            className={`w-full text-left py-1.5 px-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer ${
                              mediaMode === 'visual' && activeSubTab === 'visual' 
                                ? 'bg-amber-100/70 text-[#7A470E] font-bold' 
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <FileText className="w-3 h-3 text-[#0B2644]" />
                            <span>Visual lesson & Guide</span>
                          </button>

                          <button
                            onClick={() => setActiveSubTab('practice')}
                            className={`w-full text-left py-1.5 px-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer ${
                              activeSubTab === 'practice' 
                                ? 'bg-amber-100/70 text-[#7A470E] font-bold' 
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <Edit3 className="w-3 h-3 text-amber-700" />
                            <span>Practice exercise & Notes</span>
                          </button>

                          <button
                            onClick={() => setActiveSubTab('quiz')}
                            className={`w-full text-left py-1.5 px-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer ${
                              activeSubTab === 'quiz' 
                                ? 'bg-amber-100/70 text-[#7A470E] font-bold' 
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <HelpCircle className="w-3 h-3 text-purple-700" />
                            <span>Knowledge check</span>
                          </button>

                          <button
                            onClick={() => setActiveSubTab('flashcards')}
                            className={`w-full text-left py-1.5 px-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer ${
                              activeSubTab === 'flashcards' 
                                ? 'bg-amber-100/70 text-[#7A470E] font-bold' 
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <Bookmark className="w-3 h-3 text-blue-700" />
                            <span>Concept flashcards</span>
                          </button>

                        </div>
                      )}

                    </div>
                  );
                })}
              </div>

            </aside>
          )}

          {/* ===================================================== */}
          {/* MAIN STAGE: VIDEO / SLIDE VIEWER & PEDAGOGY TABS     */}
          {/* ===================================================== */}
          <main className="flex-1 min-w-0 space-y-6">
            
            {/* Top Lesson Header & Status Tag */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-blue-50 text-blue-900 border border-blue-200 font-sc">
                  Module 0{activeModuleIndex + 1} of 0{modules.length}
                </span>
                
                <span className="px-3 py-1 rounded-full text-xs font-black font-sc bg-amber-100 text-[#7A470E] border border-amber-300 flex items-center space-x-1.5 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-[#7A470E] animate-ping" />
                  <span>In Progress</span>
                </span>
              </div>

              <h1 className="text-2xl sm:text-4xl font-black text-[#0B2644] font-sc leading-tight">
                {currentModule?.title}
              </h1>

              {/* Teaching Faculty Course Facilitator Card for this Module */}
              <div className="bg-gradient-to-r from-amber-50/70 via-white to-slate-50 rounded-2xl border-2 border-[#7A470E]/30 p-3.5 sm:p-4 shadow-2xs flex flex-wrap items-center justify-between gap-3">
                {/* Left: Avatar, Name & Academic Rank */}
                <div className="flex items-center space-x-3.5">
                  <div className="relative">
                    <img 
                      src={currentFacilitator?.avatar} 
                      alt={currentFacilitator?.name} 
                      className="w-13 h-13 rounded-2xl object-cover border-2 border-[#7A470E] shadow-xs shrink-0" 
                    />
                    <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white shadow-xs" title="Online & In Chambers" />
                  </div>

                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-[10px] font-mono px-2 py-0.2 rounded-full bg-[#7A470E] text-white font-bold uppercase tracking-wider">
                        Course Facilitator
                      </span>
                      <span className="text-[11px] text-emerald-700 font-mono font-bold flex items-center space-x-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span>{currentFacilitator?.status || 'Online'}</span>
                      </span>
                    </div>

                    <h3 className="text-base font-black font-sc text-[#0B2644] mt-0.5 leading-snug">
                      {currentFacilitator?.name}
                    </h3>

                    <p className="text-xs text-slate-600 font-sc">
                      {currentFacilitator?.rank}
                    </p>
                  </div>
                </div>

                {/* Right: Direct Contact & In-LMS Message Button */}
                <div className="flex flex-wrap items-center gap-2">
                  <a 
                    href={`mailto:${currentFacilitator?.email}`}
                    className="px-2.5 py-1.5 rounded-xl bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-mono flex items-center space-x-1.5 transition-colors cursor-pointer shadow-2xs"
                    title={`Email ${currentFacilitator?.email}`}
                  >
                    <Mail className="w-3.5 h-3.5 text-[#7A470E]" />
                    <span className="hidden md:inline">{currentFacilitator?.email}</span>
                    <span className="md:hidden">Email</span>
                  </a>

                  <a 
                    href={`https://wa.me/${currentFacilitator?.phone?.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-mono font-bold flex items-center space-x-1.5 transition-colors cursor-pointer shadow-2xs"
                    title="Direct Mobile / WhatsApp"
                  >
                    <Phone className="w-3.5 h-3.5 text-emerald-600" />
                    <span>{currentFacilitator?.phone}</span>
                  </a>

                  <button
                    onClick={() => setFacultyModalOpen(true)}
                    className="px-3.5 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc flex items-center space-x-1.5 transition-all shadow-xs cursor-pointer"
                    title="Message Facilitator & Get In-LMS Feedback"
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-amber-300" />
                    <span>Message Facilitator</span>
                    {currentFacilitator?.unreadFeedbackCount > 0 && (
                      <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-mono font-bold flex items-center justify-center">
                        {currentFacilitator.unreadFeedbackCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Module Metadata Badges */}
              <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 font-mono font-medium border border-slate-200 flex items-center space-x-1">
                  <Clock className="w-3 h-3 text-[#7A470E]" />
                  <span>{currentModule?.duration}</span>
                </span>

                <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-900 font-bold border border-emerald-200 flex items-center space-x-1 font-sc">
                  <ShieldCheck className="w-3 h-3 text-emerald-600" />
                  <span>Senate Approved Executive Postgraduate Content</span>
                </span>
              </div>
            </div>

            {/* =================================================== */}
            {/* MEDIA CANVAS: VIDEO PLAYER OR VISUAL GUIDE SLIDE   */}
            {/* =================================================== */}
            {mediaMode === 'video' ? (
              <div className="bg-slate-950 rounded-3xl overflow-hidden border-4 border-slate-800 shadow-2xl relative">
                
                {/* 16:9 Aspect Video Player */}
                <div className="relative aspect-video w-full flex items-center justify-center bg-gradient-to-b from-slate-900 via-black to-slate-950 select-none">
                  
                  {isPlayingVideo ? (
                    /* Active Video Player State */
                    <div className="w-full h-full flex flex-col justify-between p-6 relative">
                      
                      {/* Top Bar inside Video */}
                      <div className="flex items-center justify-between text-white text-xs z-10">
                        <div className="flex items-center space-x-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                          <span className="font-sc font-bold tracking-wider uppercase text-[11px] text-[#B87A28]">
                            AKSU Smart Amphitheatre • 1080p Executive Feed
                          </span>
                        </div>
                        <span className="font-mono text-slate-300 text-xs bg-black/40 px-2 py-0.5 rounded">
                          Watermark: {student.matricNo}
                        </span>
                      </div>

                      {/* Center Graphic */}
                      <div className="text-center space-y-3 my-auto z-10">
                        <div className="w-16 h-16 rounded-full bg-[#7A470E]/80 text-white flex items-center justify-center mx-auto shadow-2xl animate-pulse">
                          <Video className="w-8 h-8" />
                        </div>
                        <h4 className="text-xl sm:text-2xl font-black font-sc text-white">
                          {currentModule?.number}: {currentModule?.title}
                        </h4>
                        <p className="text-xs text-slate-300 font-mono">
                          Lecturer: {activeCourse.lecturer} • Live Recorded at Main Campus, Ikot Akpaden
                        </p>
                      </div>

                      {/* Scrubber & Controls inside Video */}
                      <div className="space-y-2 pt-4 z-10 bg-gradient-to-t from-black/80 to-transparent p-2 rounded-xl">
                        {/* Progress Bar */}
                        <div 
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const pos = (e.clientX - rect.left) / rect.width;
                            setVideoTimestamp(Math.round(pos * 55));
                          }}
                          className="w-full bg-slate-700/80 h-2 rounded-full overflow-hidden cursor-pointer relative group"
                        >
                          <div 
                            className="bg-[#7A470E] h-2 rounded-full transition-all"
                            style={{ width: `${(videoTimestamp / 55) * 100}%` }}
                          />
                        </div>

                        {/* Controls Bottom Row */}
                        <div className="flex items-center justify-between text-white text-xs pt-1">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => setIsPlayingVideo(false)}
                              className="p-1 hover:text-[#B87A28] transition-colors cursor-pointer"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                            <span className="font-mono text-[11px] text-slate-300">
                              {videoTimestamp}:14 / {currentModule?.duration}
                            </span>
                          </div>

                          <div className="flex items-center space-x-3 text-[11px] font-mono">
                            <button
                              onClick={() => {
                                const speeds = ['1.0x', '1.25x', '1.5x'];
                                const nextIndex = (speeds.indexOf(videoSpeed) + 1) % speeds.length;
                                setVideoSpeed(speeds[nextIndex]);
                              }}
                              className="bg-white/10 hover:bg-white/20 px-2 py-0.5 rounded cursor-pointer transition-colors"
                            >
                              Speed: {videoSpeed}
                            </button>
                            <span className="bg-emerald-600/80 px-2 py-0.5 rounded text-white font-bold">1080p HD</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  ) : (
                    /* Initial Video Poster & Big Play Button (Like TrustStack Screenshot) */
                    <div className="text-center p-6 sm:p-10 space-y-4 max-w-xl mx-auto">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setIsPlayingVideo(true)}
                          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-[#7A470E] hover:to-[#5C3306] text-white flex items-center justify-center mx-auto shadow-2xl transition-all hover:scale-110 cursor-pointer group border-4 border-white/20"
                        >
                          <Play className="w-10 h-10 sm:w-12 sm:h-12 ml-1.5 text-white group-hover:scale-105" />
                        </button>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[11px] font-mono text-[#B87A28] font-bold uppercase tracking-widest block font-sc">
                          AKSU Executive Masterclass Series
                        </span>
                        <h3 className="text-xl sm:text-2xl font-black font-sc text-white">
                          {currentModule?.title}
                        </h3>
                        <p className="text-xs text-slate-300 max-w-md mx-auto">
                          Key Focus: {currentModule?.keyTakeaway}
                        </p>
                      </div>

                      <div className="pt-2 flex items-center justify-center space-x-4 text-xs text-slate-400 font-mono">
                        <span>Duration: {currentModule?.duration}</span>
                        <span>•</span>
                        <span>Faculty: {activeCourse.lecturer}</span>
                      </div>
                    </div>
                  )}

                  {/* Watermark in corner */}
                  <div className="absolute bottom-3 right-4 text-[10px] text-white/40 font-mono pointer-events-none">
                    AKSU LMS • {student.degreeLevel} Stream
                  </div>

                </div>

              </div>
            ) : (
              /* Visual Lesson & Slide Deck Stage */
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-[#7A470E]" />
                    <h3 className="text-xl font-black font-sc text-[#0B2644]">
                      Visual Guide & Slide Deck: {currentModule?.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => setMediaMode('video')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold font-sc flex items-center space-x-1 cursor-pointer"
                  >
                    <Play className="w-3.5 h-3.5 text-[#7A470E]" />
                    <span>Switch to Video</span>
                  </button>
                </div>

                {/* Illustrated Framework Card */}
                <div className="translucent-card-light rounded-2xl p-6 border-2 border-[#7A470E]/30 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                      Core Methodological Blueprint
                    </span>
                    <span className="text-[11px] bg-[#0B2644] text-white px-2 py-0.5 rounded font-mono">
                      Exhibit 2.1
                    </span>
                  </div>

                  <h4 className="text-lg font-black font-sc text-[#0B2644]">
                    The Ontological Continuum in Corporate Decision Systems
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 text-xs">
                    <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-1.5 shadow-2xs">
                      <strong className="text-slate-900 block font-sc text-sm">1. Objective Realism</strong>
                      <p className="text-slate-600 leading-relaxed">
                        Organisations exist as concrete structures independent of managerial interpretation. Measurement via hard financial indicators and operational KPIs.
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200 space-y-1.5 shadow-2xs">
                      <strong className="text-[#7A470E] block font-sc text-sm">2. Critical Realism</strong>
                      <p className="text-slate-700 leading-relaxed">
                        Acknowledges underlying generative mechanisms (e.g. power dynamics, supply chain volatility) that cause observable business events.
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-1.5 shadow-2xs">
                      <strong className="text-slate-900 block font-sc text-sm">3. Social Constructionism</strong>
                      <p className="text-slate-600 leading-relaxed">
                        Corporate strategy and culture are created and sustained through human interaction, language, and executive storytelling.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
                    <span>Source: Saunders, Lewis & Thornhill (Research Methods for Business Students)</span>
                    <button 
                      onClick={() => alert("Downloading Saunders Research Onion Executive Reference Guide (PDF)...")}
                      className="text-[#7A470E] hover:text-[#5C3306] font-bold font-sc flex items-center space-x-1 cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download Annotated Exhibit PDF (2.1 MB)</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================== */}
            {/* MODULE NAVIGATION BAR (Under Media Player)          */}
            {/* =================================================== */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              
              {/* Previous Module Button */}
              <button
                disabled={activeModuleIndex === 0}
                onClick={() => {
                  if (activeModuleIndex > 0) {
                    setActiveModuleIndex(activeModuleIndex - 1);
                    setIsPlayingVideo(false);
                  }
                }}
                className={`px-4 py-2 rounded-xl text-xs font-bold font-sc flex items-center space-x-1.5 transition-colors ${
                  activeModuleIndex === 0
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-2xs cursor-pointer'
                }`}
              >
                <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                <span>Previous module</span>
              </button>

              {/* Center Mode Switcher (Video Lecture vs Visual Lesson) */}
              <div className="flex items-center space-x-1 bg-slate-200/80 p-1 rounded-2xl border border-slate-300 text-xs font-sc font-bold">
                <button
                  onClick={() => setMediaMode('video')}
                  className={`px-4 py-1.5 rounded-xl transition-all cursor-pointer flex items-center space-x-1.5 ${
                    mediaMode === 'video'
                      ? 'bg-white text-[#0B2644] shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Play className="w-3 h-3 text-[#7A470E]" />
                  <span>Video Lecture</span>
                </button>

                <button
                  onClick={() => setMediaMode('visual')}
                  className={`px-4 py-1.5 rounded-xl transition-all cursor-pointer flex items-center space-x-1.5 ${
                    mediaMode === 'visual'
                      ? 'bg-white text-[#0B2644] shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <FileText className="w-3 h-3 text-[#7A470E]" />
                  <span>Visual Lesson</span>
                </button>
              </div>

              {/* Next Module Button */}
              <button
                disabled={activeModuleIndex === modules.length - 1}
                onClick={() => {
                  if (activeModuleIndex < modules.length - 1) {
                    setActiveModuleIndex(activeModuleIndex + 1);
                    setIsPlayingVideo(false);
                  }
                }}
                className={`px-4 py-2 rounded-xl text-xs font-bold font-sc flex items-center space-x-1.5 transition-colors ${
                  activeModuleIndex === modules.length - 1
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-[#0B2644] hover:bg-[#06182B] text-white shadow-xs cursor-pointer'
                }`}
              >
                <span>Next module</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

            </div>

            {/* =================================================== */}
            {/* HORIZONTAL PEDAGOGY TABS (Exact TrustStack Adaptation) */}
            {/* =================================================== */}
            <div className="bg-white rounded-3xl border-2 border-slate-200 px-3 sm:px-4 pt-3 pb-3.5 shadow-xs tabs-scroll-container">
              <div className="flex items-center space-x-1.5 min-w-max text-xs font-sc font-bold mb-1">
                {[
                  { id: 'overview', label: 'Overview', icon: BookOpen },
                  { id: 'video', label: 'Video lecture', icon: Play },
                  { id: 'visual', label: 'Visual lesson', icon: FileText },
                  { id: 'practice', label: 'Practice & Notes', icon: Edit3 },
                  { id: 'quiz', label: 'Knowledge check', icon: HelpCircle },
                  { id: 'flashcards', label: 'Flashcards', icon: Bookmark },
                  { id: 'faculty-desk', label: 'Faculty Desk & Feedback', icon: MessageSquare, badge: currentFacilitator?.unreadFeedbackCount > 0 ? 'Feedback' : 'Direct' },
                  { id: 'silicon', label: 'Ask Silicon (AI)', icon: BrainCircuit, badge: 'AI' },
                  { id: 'resources', label: 'Resources & Guide', icon: Download },
                  { id: 'certificate', label: 'Progress & Certificate', icon: Award }
                ].map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeSubTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveSubTab(tab.id);
                      }}
                      className={`px-3.5 py-2.5 rounded-2xl transition-all cursor-pointer flex items-center space-x-1.5 whitespace-nowrap ${
                        isActive
                          ? 'bg-[#0B2644] text-white shadow-xs font-bold'
                          : 'text-slate-700 hover:bg-slate-100 hover:text-[#0B2644]'
                      }`}
                    >
                      <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-amber-300' : 'text-slate-400'}`} />
                      <span>{tab.label}</span>
                      {tab.badge && (
                        <span className="px-1.5 py-0.2 rounded text-[9px] bg-purple-100 text-purple-900 font-mono font-black">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* =================================================== */}
            {/* TAB CONTENTS PANELS                                 */}
            {/* =================================================== */}
            
            {/* TAB: OVERVIEW */}
            {activeSubTab === 'overview' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    Module Synopsis & Executive Learning Outcomes
                  </h3>
                  <span className="text-xs text-[#7A470E] font-bold font-sc">
                    Module 0{activeModuleIndex + 1}
                  </span>
                </div>

                <div className="prose prose-sm max-w-none text-slate-700 space-y-4 text-xs sm:text-sm leading-relaxed">
                  <p>
                    In <strong>{currentModule?.title}</strong>, scholars explore the fundamental philosophical 
                    assumptions underpinning executive inquiry in Business Administration. Rather than treating methodology 
                    as a mere set of tools, this module examines how ontological positions (what is reality?) govern 
                    subsequent epistemological standards (what is defensible evidence?).
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
                    <div className="translucent-card-light p-4 rounded-2xl border border-slate-200 space-y-2">
                      <strong className="text-[#0B2644] block font-sc">Key Learning Objectives:</strong>
                      <ul className="space-y-1.5 text-xs text-slate-600 list-disc pl-4">
                        <li>Differentiate between Objectivism, Relativism, and Critical Realism.</li>
                        <li>Map your chosen industry dissertation dilemma onto Saunders' Research Onion.</li>
                        <li>Formulate research questions that satisfy Senate academic scrutiny and boardroom value.</li>
                      </ul>
                    </div>

                    <div className="translucent-card-gold p-4 rounded-2xl border border-[#7A470E]/30 space-y-2">
                      <strong className="text-[#7A470E] block font-sc">Continuous Assessment Weight:</strong>
                      <p className="text-xs text-slate-700">
                        Attendance & Masterclass engagement: <strong>10%</strong><br />
                        Knowledge Check & Case Exercises: <strong>15%</strong><br />
                        Preliminary Term 1 Concept Note: <strong>25%</strong>
                      </p>
                    </div>
                  </div>

                  <p>
                    <strong>Recommended Reading:</strong> Saunders, M., Lewis, P., & Thornhill, A. (2025). 
                    <em> Research Methods for Business Students</em> (9th ed.). Pearson Education. Chapter 4: Understanding research philosophies.
                  </p>
                </div>
              </div>
            )}

            {/* TAB: VIDEO LECTURE TRANSCRIPT */}
            {activeSubTab === 'video' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    Interactive Lecture Transcript & Timestamps
                  </h3>
                  <span className="text-xs text-slate-500 font-mono">
                    Speed: {videoSpeed} • HD Audio
                  </span>
                </div>

                <div className="space-y-3 text-xs sm:text-sm">
                  {[
                    { time: '00:00', speaker: 'Prof. Asuquo E. Johnson', text: 'Welcome, distinguished executive scholars. Today we examine the ontological bedrock of your doctoral and master’s research.' },
                    { time: '12:30', speaker: 'Prof. Asuquo E. Johnson', text: 'Consider a petroleum supply chain in the Niger Delta. Is the disruption an objective fact you measure with throughput tonnage, or is it interpreted through stakeholder perceptions?' },
                    { time: '28:45', speaker: 'Dr Uwem Etim Uwah', text: 'Intervention: From a financial auditing perspective, remember that ontology dictates which accounting metrics can be accepted as empirical facts.' },
                    { time: '42:10', speaker: 'Prof. Asuquo E. Johnson', text: 'This brings us to Saunders’ Onion: your philosophical layer must directly align with your methodological choice.' }
                  ].map((line, idx) => (
                    <div 
                      key={idx}
                      onClick={() => {
                        const mins = parseInt(line.time.split(':')[0], 10);
                        setVideoTimestamp(mins);
                        setMediaMode('video');
                        setIsPlayingVideo(true);
                      }}
                      className="p-3 rounded-xl hover:bg-amber-50/60 transition-colors flex items-start space-x-3 cursor-pointer group border border-transparent hover:border-[#7A470E]/30"
                    >
                      <span className="font-mono text-xs font-bold text-[#7A470E] group-hover:underline shrink-0 mt-0.5">
                        {line.time}
                      </span>
                      <div>
                        <strong className="text-[#0B2644] font-sc block">{line.speaker}:</strong>
                        <p className="text-slate-600 mt-0.5">{line.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB: VISUAL LESSON */}
            {activeSubTab === 'visual' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    Visual Lesson & Conceptual Slide Guide
                  </h3>
                  <button 
                    onClick={() => setMediaMode('visual')}
                    className="text-xs font-bold text-[#7A470E] font-sc flex items-center space-x-1 cursor-pointer"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>Expand Slide Player</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs sm:text-sm">
                  <div className="translucent-card-light p-5 rounded-2xl border border-slate-200 space-y-3">
                    <span className="text-[11px] font-black uppercase text-[#7A470E] font-sc">Slide 1: Ontology Matrix</span>
                    <h4 className="font-black text-slate-900 font-sc">Realism vs Nominalism in Practice</h4>
                    <p className="text-slate-600 leading-relaxed text-xs">
                      Realism asserts that social entities exist in an objective reality. In business, market share, inflation rates, and supply delivery delays are treated as measurable realities. Nominalism counters that social reality is simply a collection of names and concepts created by humans to understand complex events.
                    </p>
                  </div>

                  <div className="translucent-card-gold p-5 rounded-2xl border border-[#7A470E]/30 space-y-3">
                    <span className="text-[11px] font-black uppercase text-[#7A470E] font-sc">Slide 2: Saunders' Onion</span>
                    <h4 className="font-black text-slate-900 font-sc">Peeling Methodological Layers</h4>
                    <p className="text-slate-700 leading-relaxed text-xs">
                      1. Philosophy (Critical Realism) → 2. Approach (Deductive/Inductive) → 3. Strategy (Case Study) → 4. Choice (Mixed Methods) → 5. Time Horizon (Cross-sectional) → 6. Techniques (Interviews + Econometrics).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: PRACTICE EXERCISE & NOTES */}
            {activeSubTab === 'practice' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <h3 className="text-xl font-black font-sc text-[#0B2644]">
                      Executive Scholar Digital Notepad
                    </h3>
                    <p className="text-xs text-slate-500">
                      Take structured notes and draft your preliminary research stance. Synchronized with your student profile.
                    </p>
                  </div>
                  
                  <button
                    onClick={handleSaveNotes}
                    className="px-4 py-2 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold text-xs rounded-xl shadow-xs transition-colors flex items-center space-x-1.5 cursor-pointer font-sc"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>{notesSaved ? 'Saved to Cloud!' : 'Save Notes'}</span>
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="p-4 rounded-2xl bg-amber-50/60 border border-amber-200 text-xs text-slate-700">
                    <strong className="text-[#7A470E] block font-sc mb-1">Doctoral Guided Exercise:</strong>
                    "Articulate the ontological position you intend to adopt for your proposed dissertation research topic: <em>{student.researchTopic}</em>. Explain whether you view organisational resilience as an objective capability or a socially negotiated leadership culture."
                  </div>

                  <textarea
                    rows={7}
                    value={scholarNotes}
                    onChange={(e) => setScholarNotes(e.target.value)}
                    className="w-full p-4 rounded-2xl border border-slate-300 focus:outline-none focus:border-[#7A470E] focus:ring-1 focus:ring-[#7A470E] text-xs sm:text-sm font-sans leading-relaxed"
                    placeholder="Enter your executive lecture notes, literature citations, or dissertation reflections here..."
                  />

                  <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
                    <span>Last edited: Just now • Word count: {scholarNotes.split(/\s+/).filter(Boolean).length} words</span>
                    <button 
                      onClick={() => alert("Notes exported as PDF for Prof. Johnson review.")}
                      className="text-[#7A470E] hover:underline font-sc font-bold flex items-center space-x-1 cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Export as PDF</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: KNOWLEDGE CHECK (QUIZ) */}
            {activeSubTab === 'quiz' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <h3 className="text-xl font-black font-sc text-[#0B2644]">
                      Module Knowledge Check (Continuous Assessment)
                    </h3>
                    <p className="text-xs text-slate-500">
                      Answer the 3 peer-reviewed case questions to validate your philosophical comprehension.
                    </p>
                  </div>

                  {quizSubmitted && (
                    <span className="px-3 py-1 rounded-full text-xs font-black font-sc bg-emerald-100 text-emerald-900 border border-emerald-300">
                      Score: {quizScore} / {quizQuestions.length} ({Math.round((quizScore / quizQuestions.length) * 100)}%)
                    </span>
                  )}
                </div>

                <div className="space-y-6">
                  {quizQuestions.map((q, qIndex) => {
                    const selected = quizAnswers[q.id];
                    const isCorrect = selected === q.correctKey;

                    return (
                      <div 
                        key={q.id}
                        className={`p-5 rounded-2xl border transition-all ${
                          quizSubmitted
                            ? isCorrect
                              ? 'bg-emerald-50/40 border-emerald-300'
                              : 'bg-red-50/40 border-red-300'
                            : 'bg-slate-50/60 border-slate-200'
                        }`}
                      >
                        <h4 className="font-bold text-sm text-slate-900 font-sc mb-3">
                          Question {qIndex + 1}: {q.question}
                        </h4>

                        <div className="space-y-2">
                          {q.options.map((opt) => {
                            const isOptionSelected = selected === opt.key;

                            return (
                              <button
                                key={opt.key}
                                onClick={() => handleQuizSelect(q.id, opt.key)}
                                className={`w-full text-left p-3 rounded-xl border text-xs flex items-center space-x-3 transition-all cursor-pointer ${
                                  isOptionSelected
                                    ? 'bg-[#0B2644] text-white border-[#0B2644] shadow-xs'
                                    : 'bg-white text-slate-700 hover:bg-slate-100 border-slate-200'
                                }`}
                              >
                                <span className={`w-5 h-5 rounded-md flex items-center justify-center font-bold text-xs shrink-0 font-mono ${
                                  isOptionSelected ? 'bg-amber-400 text-[#06182B]' : 'bg-slate-100 text-slate-700'
                                }`}>
                                  {opt.key}
                                </span>
                                <span>{opt.text}</span>
                              </button>
                            );
                          })}
                        </div>

                        {quizSubmitted && (
                          <div className="mt-3 p-3 rounded-xl bg-white/90 border text-xs text-slate-600">
                            <strong className={isCorrect ? 'text-emerald-700 font-sc' : 'text-red-700 font-sc'}>
                              {isCorrect ? '✓ Correct Answer!' : `✗ Incorrect (Correct: ${q.correctKey})`}
                            </strong>
                            <p className="mt-1 text-slate-600">{q.explanation}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <span className="text-xs text-slate-500 font-mono">
                    Questions answered: {Object.keys(quizAnswers).length} / {quizQuestions.length}
                  </span>

                  <div className="space-x-3">
                    {quizSubmitted ? (
                      <button
                        onClick={handleResetQuiz}
                        className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs font-sc cursor-pointer"
                      >
                        Retake Assessment
                      </button>
                    ) : (
                      <button
                        disabled={Object.keys(quizAnswers).length < quizQuestions.length}
                        onClick={handleQuizSubmit}
                        className="px-5 py-2.5 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] disabled:opacity-50 text-white font-bold text-xs font-sc transition-colors cursor-pointer shadow-xs"
                      >
                        Submit Assessment
                      </button>
                    )}
                  </div>
                </div>

              </div>
            )}

            {/* TAB: FLASHCARDS */}
            {activeSubTab === 'flashcards' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <h3 className="text-xl font-black font-sc text-[#0B2644]">
                      Doctoral Methodology Flashcards
                    </h3>
                    <p className="text-xs text-slate-500">
                      Click card to flip between definition and executive research application.
                    </p>
                  </div>
                  <span className="text-xs font-mono font-bold text-[#7A470E]">
                    Card {currentCardIndex + 1} of {flashcards.length}
                  </span>
                </div>

                {/* Flip Card Container */}
                <div 
                  onClick={() => setIsCardFlipped(!isCardFlipped)}
                  className="w-full min-h-[220px] rounded-3xl p-8 border-2 border-[#7A470E]/40 translucent-card-light flex flex-col justify-between cursor-pointer select-none hover:shadow-xl transition-all relative overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                      {isCardFlipped ? 'Definition & Boardroom Application' : 'Core Methodological Term'}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">
                      (Click to flip)
                    </span>
                  </div>

                  <div className="my-auto text-center py-4">
                    {!isCardFlipped ? (
                      <div className="space-y-2">
                        <h4 className="text-3xl font-black font-sc text-[#0B2644]">
                          {flashcards[currentCardIndex].term}
                        </h4>
                        <p className="text-xs text-slate-500">
                          {flashcards[currentCardIndex].question}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3 text-left">
                        <p className="text-sm text-slate-800 leading-relaxed font-medium">
                          {flashcards[currentCardIndex].definition}
                        </p>
                        <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200 text-xs text-slate-700">
                          <strong className="text-[#7A470E] font-sc block">Dissertation Context:</strong>
                          {flashcards[currentCardIndex].context}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-center text-[11px] text-slate-400 font-sc">
                    Tap anywhere on card to {isCardFlipped ? 'view term' : 'reveal meaning'}
                  </div>
                </div>

                {/* Flashcard Navigation */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    disabled={currentCardIndex === 0}
                    onClick={() => {
                      if (currentCardIndex > 0) {
                        setCurrentCardIndex(currentCardIndex - 1);
                        setIsCardFlipped(false);
                      }
                    }}
                    className="px-4 py-2 rounded-xl text-xs font-bold font-sc border border-slate-200 disabled:opacity-40 cursor-pointer"
                  >
                    ← Previous Term
                  </button>

                  <button
                    disabled={currentCardIndex === flashcards.length - 1}
                    onClick={() => {
                      if (currentCardIndex < flashcards.length - 1) {
                        setCurrentCardIndex(currentCardIndex + 1);
                        setIsCardFlipped(false);
                      }
                    }}
                    className="px-4 py-2 rounded-xl bg-[#0B2644] text-white text-xs font-bold font-sc disabled:opacity-40 cursor-pointer"
                  >
                    Next Term →
                  </button>
                </div>

              </div>
            )}

            {/* TAB: ASK SILICON AI EMBEDDED */}
            {activeSubTab === 'silicon' && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="bg-white rounded-3xl border-2 border-purple-200 p-5 sm:p-6 shadow-sm flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-11 h-11 rounded-2xl bg-purple-100 flex items-center justify-center text-purple-900 shrink-0">
                      <BrainCircuit className="w-6 h-6 text-purple-700" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black font-sc text-[#0B2644]">
                        Silicon AI Research Learning Assistant
                      </h3>
                      <p className="text-xs text-slate-500">
                        Ask conceptual, philosophical, or methodological questions for <em>{currentModule?.title}</em>.
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedSiliconPrompt(null);
                      setIsSiliconOpen(true);
                    }}
                    className="px-4 py-2 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold text-xs font-sc transition-colors cursor-pointer shadow-xs"
                  >
                    Open Floating Window
                  </button>
                </div>

                {/* Embedded Inline Silicon AI Workspace */}
                <SiliconAssistant
                  isInline={true}
                  currentStudent={student}
                  activeModule={currentModule}
                  activeCourse={activeCourse}
                  studentDegree={student.degreeLevel}
                />
              </div>
            )}

            {/* TAB: RESOURCES & GUIDES */}
            {activeSubTab === 'resources' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    Official Course Handbook & Downloadable Packet
                  </h3>
                  <span className="text-xs text-[#7A470E] font-bold font-sc">
                    {activeCourse.materials?.length || 4} Core Documents
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(activeCourse.materials || []).map((mat, mIdx) => (
                    <div 
                      key={mIdx}
                      className="p-4 rounded-2xl border border-slate-200 hover:border-[#7A470E] transition-all flex items-start justify-between gap-3 group bg-white shadow-2xs"
                    >
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#7A470E] font-sc">
                          {mat.category}
                        </span>
                        <h4 className="text-sm font-bold text-slate-900 group-hover:text-[#0B2644] font-sc leading-snug">
                          {mat.title}
                        </h4>
                        <div className="text-[11px] text-slate-500 font-mono">
                          {mat.type} • {mat.size}
                        </div>
                      </div>

                      <button
                        onClick={() => alert(`Downloading: ${mat.title}`)}
                        className="p-2 rounded-xl bg-slate-100 hover:bg-[#7A470E] text-slate-700 hover:text-white transition-colors cursor-pointer shrink-0 mt-1"
                        title="Download Document"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB: PROGRESS & CERTIFICATE */}
            {activeSubTab === 'certificate' && (
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <h3 className="text-xl font-black font-sc text-[#0B2644]">
                      Academic Progress & Postgraduate Transcript Status
                    </h3>
                    <p className="text-xs text-slate-500">
                      Senate requirements for {student.programmeName}
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-black font-sc bg-amber-100 text-[#7A470E] border border-amber-300">
                    Term 1 Intake Active
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center space-y-1">
                    <span className="text-xs font-bold text-slate-500 font-sc">Total Degree Credits</span>
                    <div className="text-3xl font-black text-[#0B2644] font-mono">{student.totalCredits}</div>
                    <span className="text-[11px] text-slate-400">Senate Approved Units</span>
                  </div>

                  <div className="p-4 rounded-2xl bg-amber-50/70 border border-amber-200 text-center space-y-1">
                    <span className="text-xs font-bold text-[#7A470E] font-sc">Current Enrolled Load</span>
                    <div className="text-3xl font-black text-[#7A470E] font-mono">{student.enrolledCredits}</div>
                    <span className="text-[11px] text-slate-600">{activeCourse.code} (3 Units)</span>
                  </div>

                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center space-y-1">
                    <span className="text-xs font-bold text-slate-500 font-sc">Attendance Verified</span>
                    <div className="text-3xl font-black text-emerald-700 font-mono">100%</div>
                    <span className="text-[11px] text-emerald-800">Smart Amphitheatre Logs</span>
                  </div>
                </div>

                {/* Certificate Gate Checklist */}
                <div className="p-5 rounded-2xl bg-slate-50/80 border border-slate-200 space-y-3">
                  <h4 className="font-bold text-sm text-[#0B2644] font-sc">Module 1 Completion Gates:</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center space-x-2 text-emerald-800">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span>Lecture 1 (Foundations of Scientific Inquiry) — Completed</span>
                    </div>
                    <div className="flex items-center space-x-2 text-amber-800">
                      <span className="w-4 h-4 rounded-full border-2 border-amber-500 flex items-center justify-center font-bold text-[10px]">
                        •
                      </span>
                      <span>Lecture 2 (Ontological Positions) — In Progress (Current Module)</span>
                    </div>
                    <div className="flex items-center space-x-2 text-slate-500">
                      <Lock className="w-4 h-4 text-slate-400" />
                      <span>Lectures 3 to 6 — Scheduled for Alternate Weekend Cohort</span>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB: FACULTY MESSAGING & FEEDBACK DESK */}
            {activeSubTab === 'faculty-desk' && (
              <FacultyMessagingDesk 
                facilitator={currentFacilitator}
                currentModule={currentModule}
                course={activeCourse}
                student={student}
              />
            )}

          </main>

          {/* ======================================================== */}
          {/* COLUMN 3: SILICON AI ASSISTANT (Curved Rectangle Card)   */}
          {/* Docked directly in-flow beside main stage with 0 overlap */}
          {/* ======================================================== */}
          {isSiliconOpen && (
            <aside className="fixed inset-4 sm:inset-8 lg:static lg:inset-auto lg:w-[420px] xl:w-[460px] 2xl:w-[490px] shrink-0 lg:sticky lg:top-28 h-[88vh] lg:h-[calc(100vh-8.5rem)] bg-white rounded-3xl border-2 border-[#7A470E] shadow-2xl z-50 lg:z-10 flex flex-col overflow-hidden animate-in slide-in-from-right duration-300 ring-1 ring-black/5">
              <SiliconAssistant 
                isOpen={true}
                onClose={() => setIsSiliconOpen(false)}
                onToggle={() => setIsSiliconOpen(false)}
                currentStudent={student}
                activeModule={currentModule}
                activeCourse={activeCourse}
                initialPrompt={selectedSiliconPrompt}
                isInline={true}
              />
            </aside>
          )}

        </div>
      </div>

      {/* ======================================================== */}
      {/* IN-LMS FACULTY CONSULTATION & FEEDBACK MODAL             */}
      {/* ======================================================== */}
      {facultyModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
          <FacultyMessagingDesk 
            facilitator={currentFacilitator}
            currentModule={currentModule}
            course={activeCourse}
            student={student}
            isModal={true}
            onClose={() => setFacultyModalOpen(false)}
          />
        </div>
      )}

      {/* ======================================================== */}
      {/* PERSISTENT BOTTOM-RIGHT SILICON AI LAUNCHER PILL          */}
      {/* Always visible at the bottom-right of LMS when closed    */}
      {/* ======================================================== */}
      {!isSiliconOpen && (
        <div className="fixed bottom-5 right-5 z-40 print:hidden">
          <button
            onClick={() => setIsSiliconOpen(true)}
            className="group px-4 py-3 rounded-full shadow-2xl flex items-center space-x-3 transition-all transform hover:scale-105 cursor-pointer border-2 font-sc bg-gradient-to-r from-[#030F1C] via-[#0B2644] to-[#7A470E] border-white/50 text-white hover:border-amber-300 animate-bounce-subtle"
            title="Launch Silicon Academic AI Assistant"
          >
            <div className="relative">
              <div className="w-8 h-8 rounded-full bg-white text-[#7A470E] flex items-center justify-center shadow-inner font-black text-sm">
                <Sparkles className="w-4 h-4 text-[#7A470E]" />
              </div>
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div className="text-left">
              <div className="text-xs font-black tracking-wide leading-tight flex items-center space-x-1.5">
                <span>Ask Silicon AI</span>
                <span className="text-[9px] px-1.5 py-0.2 rounded font-mono font-bold bg-white/20 text-white">
                  Online
                </span>
              </div>
              <div className="text-[10px] text-white/80 hidden sm:block">
                AKSU Research Assistant
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Candidate Account Drawer with 6 Submenus */}
      <CandidateAccountDrawer 
        isOpen={candidateDrawerOpen}
        onClose={() => setCandidateDrawerOpen(false)}
        studentId={activeStudentId}
        onSelectStudent={handleStudentSwitch}
      />

      {/* ======================================================== */}
      {/* UPDATES MODAL (TrustStack Adaptation)                    */}
      {/* ======================================================== */}
      {updatesModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-5 shadow-2xl border-2 border-slate-200 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center space-x-2">
                <Bell className="w-5 h-5 text-[#7A470E]" />
                <h3 className="text-xl font-black font-sc text-[#0B2644]">
                  LMS Programme Updates
                </h3>
              </div>
              <button 
                onClick={() => setUpdatesModalOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs sm:text-sm text-slate-700">
              <div className="p-3.5 rounded-2xl bg-amber-50/70 border border-amber-200 space-y-1">
                <span className="text-[10px] font-bold text-[#7A470E] uppercase font-mono block">Today, 08:30 AM</span>
                <strong className="text-slate-900 block font-sc">Saunders Onion Annotated Framework Uploaded</strong>
                <p className="text-xs text-slate-600">
                  Prof. Asuquo Johnson has added supplementary HBR case slides to the Visual Guide section.
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase font-mono block">Yesterday</span>
                <strong className="text-slate-900 block font-sc">Smart Amphitheatre Timetable Confirmed</strong>
                <p className="text-xs text-slate-600">
                  All Saturday morning sessions commence at 09:00 AM prompt at Executive Suite 1, Main Campus, Ikot Akpaden.
                </p>
              </div>
            </div>

            <button
              onClick={() => setUpdatesModalOpen(false)}
              className="w-full py-2.5 rounded-xl bg-[#0B2644] text-white font-bold text-xs font-sc cursor-pointer"
            >
              Dismiss Notifications
            </button>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* ABOUT COURSE SYLLABUS MODAL                             */}
      {/* ======================================================== */}
      {aboutCourseModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 space-y-5 shadow-2xl border-2 border-slate-200 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center space-x-2">
                <BookOpen className="w-5 h-5 text-[#7A470E]" />
                <h3 className="text-xl font-black font-sc text-[#0B2644]">
                  {activeCourse.code}: {activeCourse.title}
                </h3>
              </div>
              <button 
                onClick={() => setAboutCourseModalOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed max-h-[400px] overflow-y-auto pr-1">
              <div>
                <strong className="text-[#0B2644] font-sc block mb-1">Course Description:</strong>
                <p className="text-slate-600 text-xs">
                  {activeCourse.overview || "This course delivers foundational doctoral education in scientific inquiry, epistemological stances, ontological assumptions, and qualitative/quantitative research design for senior business leaders."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <span className="text-slate-400 block font-mono text-[10px]">CREDIT UNITS</span>
                  <strong className="text-[#0B2644] font-sc">{activeCourse.units || 3} Semester Units</strong>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <span className="text-slate-400 block font-mono text-[10px]">DELIVERY MODE</span>
                  <strong className="text-[#0B2644] font-sc">Executive Hybrid</strong>
                </div>
              </div>

              <div>
                <strong className="text-[#0B2644] font-sc block mb-1">Teaching Faculty:</strong>
                <p className="text-xs text-slate-600">
                  Lead Professor: {activeCourse.lecturer}<br />
                  Co-Faculty: {activeCourse.coLecturer || "Dr Uwem Etim Uwah, PhD, FCA, ACTI"}
                </p>
              </div>

              <div>
                <strong className="text-[#0B2644] font-sc block mb-1">Campus Location:</strong>
                <p className="text-xs text-slate-600">
                  Executive Suite 1, Main Campus in Ikot Akpaden, Mkpat Enin, Akwa Ibom State.
                </p>
              </div>
            </div>

            <button
              onClick={() => setAboutCourseModalOpen(false)}
              className="w-full py-2.5 rounded-xl bg-[#0B2644] text-white font-bold text-xs font-sc cursor-pointer"
            >
              Close Course Details
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
